import {
  Bell,
  BookMarked,
  ChevronDown,
  LogOut,
  Menu,
  Plus,
  User,
  CheckCircle2,
  XCircle,
  UploadCloud,
  FileText,
  Glasses,
  Inbox,
  ArrowRight,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserInfoContext";
import toast from "react-hot-toast";
import notificationService, {
  NotificationItem,
} from "../services/notification.service";
import authService from "../services/auth.service";
import Loader from "../widgets/Loader";
import { setSentryUser, addBreadcrumb } from "../lib/sentry";
import useNotificationStream from "../hooks/useNotificationStream";
import GlobalSearchBar from "../widgets/GlobalSearchBar";

interface HeaderProps {
  onMenuClick?: () => void;
  onToggleSidebarCollapse?: () => void;
  isSidebarCollapsed?: boolean;
}

/**
 * Relative time ("2 min ago", "3 hr ago", "yesterday") — used in the bell
 * dropdown where compact timestamps matter more than absolute ones.
 */
const formatRelative = (dateString: string) => {
  const diff = Date.now() - new Date(dateString).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  const d = Math.floor(hr / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d} days ago`;
  return new Date(dateString).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/**
 * Pick a coloured icon for an event_type so the bell dropdown reads like
 * a feed instead of a wall of text. Mirrors the icons used on the My
 * Notifications tab so users learn one visual language.
 */
/**
 * Bucket a notification's createdAt into a coarse time label used as a
 * section header in the bell dropdown. Keeps related notifications
 * grouped so the feed reads like Twitter / Slack rather than a flat list.
 */
function dateBucket(dateString: string): "Today" | "Yesterday" | "Earlier" {
  const now = new Date();
  const d = new Date(dateString);
  const sameDay =
    now.getFullYear() === d.getFullYear() &&
    now.getMonth() === d.getMonth() &&
    now.getDate() === d.getDate();
  if (sameDay) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    yesterday.getFullYear() === d.getFullYear() &&
    yesterday.getMonth() === d.getMonth() &&
    yesterday.getDate() === d.getDate();
  if (isYesterday) return "Yesterday";
  return "Earlier";
}

function eventIcon(eventType: string | undefined) {
  if (!eventType) return <FileText className="w-4 h-4 text-slate-500" />;
  const t = eventType.toLowerCase();
  if (t.includes("upload")) return <UploadCloud className="w-4 h-4 text-blue-500" />;
  if (t.includes("fail") || t.includes("error")) return <XCircle className="w-4 h-4 text-red-500" />;
  if (t.includes("complete") || t.includes("done") || t.includes("processed"))
    return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  return <FileText className="w-4 h-4 text-slate-500" />;
}
export default function Header({
  onMenuClick,
  onToggleSidebarCollapse,
  isSidebarCollapsed,
}: HeaderProps) {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { userInfo, isLoading } = useUser();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);

  // ⌘K / Ctrl+K is now handled inside <GlobalSearchBar /> — keeping this
  // empty so we don't double-bind the listener. (See GlobalSearchBar's own
  // useEffect for the focus shortcut.)

  const handleLogout = async () => {
    setIsLoggingOut(true);
    setShowProfileMenu(false);

    try {
      await authService.logout();
      addBreadcrumb('auth', 'User logged out');
      setSentryUser(null);
      toast.success("Logged out successfully!");
      navigate("/login");
    } catch {
      setSentryUser(null);
      toast.success("Logged out successfully!");
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".notification-dropdown")) {
        setShowNotifications(false);
      }
      if (!target.closest(".profile-dropdown")) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Total unread count — comes from the backend's `counts.unread` so the
  // bell badge stays accurate even when the dropdown only shows the first 10.
  const [unreadCount, setUnreadCount] = useState(0);
  // In-dropdown filter — toggle between every notification and just the
  // unread ones. Independent of the My Notifications tab's own filter.
  const [dropdownFilter, setDropdownFilter] = useState<"all" | "unread">("all");

  const fetchNotifications = async () => {
    setLoadingNotifications(true);
    const params = { skip: 0, limit: 10, is_read: false };
    const res = await notificationService.getNotificationList(params);
    setNotifications((res.data || []).slice(0, 10));
    setUnreadCount(res.counts?.unread ?? (res.data || []).filter((n: NotificationItem) => !n.is_read).length);
    setLoadingNotifications(false);
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Live updates — open one SSE connection while the user is logged in.
  // - notification:new   → prepend to the dropdown, bump the badge.
  // - notification:read  → drop the badge to the value the server sent
  //                        (mark-single or mark-all both flow through here).
  // The hook itself only re-opens when `enabled` flips; the latest event
  // callback is captured via a ref so it never restarts the EventSource.
  useNotificationStream((event) => {
    if (event.type === "notification:new" && event.notification) {
      setNotifications((prev) => [event.notification!, ...prev].slice(0, 10));
      if (typeof event.unread_count === "number") {
        setUnreadCount(event.unread_count);
      } else {
        setUnreadCount((c) => c + 1);
      }
    } else if (event.type === "notification:read" || event.type === "notification:badge_count") {
      if (typeof event.unread_count === "number") {
        setUnreadCount(event.unread_count);
      }
      // Sync the dropdown's `is_read` flags too so a row marked from
      // another tab visually flips.
      setNotifications((prev) =>
        event.type === "notification:read" && event.notification?.id
          ? prev.map((n) => (n.id === event.notification!.id ? { ...n, is_read: true } : n))
          : prev,
      );
    }
  }, !!userInfo?.user_id);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div
        className={`flex items-center justify-between px-4 ${
          isSidebarCollapsed ? "h-[64px]" : "h-[72px]"
        }`}
      >
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden text-gray-600 hover:text-gray-900 p-1"
          >
            <Menu className="w-5 h-5" />
          </button>

          <button
            onClick={onToggleSidebarCollapse}
            className="hidden lg:block text-gray-400 hover:text-gray-600 p-1"
            title={isSidebarCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <Menu className="text-gray-800 w-5 h-5" />
          </button>

          {/* Global search — sits at the left of the navbar, right after
              the menu / sidebar-toggle buttons. Hidden on small screens
              because the right-side actions take priority there. */}
          <div className="hidden md:block ml-2 w-[420px] max-w-[42vw]">
            <GlobalSearchBar />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => window.open("/docs", "_blank")}
            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-white border border-blue-500 rounded-lg hover:bg-blue-50 hover:ring-2 hover:ring-blue-200 hover:border-blue-600 transition-all duration-200 text-sm font-medium"
          >
            <BookMarked className="w-4 h-4" />
            Docs
          </button>

          <button
            onClick={() => navigate("/workflow/create")}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            New Workflow
          </button>

          <div className="relative notification-dropdown">
            <button
              onClick={() => {
                setShowNotifications(!showNotifications);
                fetchNotifications();
              }}
              className="relative p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <Bell className="w-6 h-6 text-gray-700" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center animate-pulse">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (() => {
              // Compute the filtered + grouped view for the dropdown body.
              const visible = notifications.filter((n) =>
                dropdownFilter === "unread" ? !n.is_read : true,
              );
              // Bucket by Today / Yesterday / Earlier, preserving original
              // (server-sorted) order inside each bucket.
              const groups: { label: string; items: NotificationItem[] }[] = [];
              const seenBucket: Record<string, number> = {};
              visible.forEach((n) => {
                const b = dateBucket(n.createdAt);
                if (!(b in seenBucket)) {
                  seenBucket[b] = groups.length;
                  groups.push({ label: b, items: [] });
                }
                groups[seenBucket[b]].items.push(n);
              });

              return (
              <div className="absolute right-0 mt-3 w-[440px] bg-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] border border-slate-200 overflow-hidden animate-[fadeIn_120ms_ease-out]">
                {/* ── Header — gradient strip, count, mark-all ─────────── */}
                <div className="px-4 py-3 bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-500 text-white">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-lg bg-white/20 backdrop-blur flex items-center justify-center">
                        <Bell className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold leading-tight">Notifications</h3>
                        <p className="text-[11px] text-blue-100 mt-0.5">
                          {unreadCount > 0
                            ? `${unreadCount} unread notification${unreadCount === 1 ? "" : "s"}`
                            : "You're all caught up"}
                        </p>
                      </div>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await notificationService.markAllAsRead();
                            await fetchNotifications();
                            toast.success("Marked all as read");
                          } catch {
                            toast.error("Couldn't mark all as read");
                          }
                        }}
                        className="inline-flex items-center gap-1 text-[11px] font-medium bg-white/15 hover:bg-white/25 px-2 py-1 rounded-md transition-colors"
                        title="Mark every notification as read"
                      >
                        <Glasses className="w-3 h-3" />
                        Mark all read
                      </button>
                    )}
                  </div>
                </div>

                {/* ── Filter pills — All / Unread ──────────────────────── */}
                <div className="flex gap-1 px-3 pt-2 pb-1 border-b border-slate-100 bg-white">
                  {([
                    { id: "all" as const, label: "All", count: notifications.length },
                    { id: "unread" as const, label: "Unread", count: unreadCount },
                  ]).map((f) => {
                    const isActive = dropdownFilter === f.id;
                    return (
                      <button
                        key={f.id}
                        type="button"
                        onClick={() => setDropdownFilter(f.id)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                          isActive
                            ? "bg-blue-100 text-blue-700"
                            : "text-slate-600 hover:bg-slate-100"
                        }`}
                      >
                        {f.label}
                        <span
                          className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${
                            isActive
                              ? "bg-blue-600 text-white"
                              : "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {f.count}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* ── Body — feed list grouped by date bucket ──────────── */}
                {/* `chat-scrollbar` is the project's slim 6px slate scrollbar
                    defined in index.css — reused here so the dropdown matches
                    the chat thread visually. */}
                <div className="max-h-[420px] overflow-y-auto chat-scrollbar">
                  {loadingNotifications ? (
                    <div className="flex flex-col items-center justify-center min-h-[200px]">
                      <Loader />
                    </div>
                  ) : visible.length === 0 ? (
                    <div className="px-6 py-12 text-center text-slate-500">
                      <Inbox className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p className="text-sm font-semibold text-slate-700">
                        {dropdownFilter === "unread"
                          ? "Nothing unread"
                          : "No notifications yet"}
                      </p>
                      <p className="text-[11px] mt-1 text-slate-400">
                        {dropdownFilter === "unread"
                          ? "You're all caught up."
                          : "You'll see uploads and processing alerts here."}
                      </p>
                    </div>
                  ) : (
                    groups.map((group) => (
                      <div key={group.label}>
                        <div className="sticky top-0 px-4 py-1.5 bg-slate-50/95 backdrop-blur text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-100">
                          {group.label}
                        </div>
                        <ul className="divide-y divide-slate-100">
                          {group.items.map((item) => (
                            <li
                              key={item.id}
                              onClick={async () => {
                                if (!item.is_read) {
                                  try {
                                    await notificationService.postNotificationRead({ ids: [item.id] });
                                    setNotifications((prev) =>
                                      prev.map((n) =>
                                        n.id === item.id ? { ...n, is_read: true } : n,
                                      ),
                                    );
                                    setUnreadCount((c) => Math.max(0, c - 1));
                                  } catch {
                                    /* ignore */
                                  }
                                }
                              }}
                              className={`relative flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${
                                item.is_read
                                  ? "bg-white hover:bg-slate-50"
                                  : "bg-blue-50/40 hover:bg-blue-50"
                              }`}
                            >
                              {/* Left accent bar for unread rows */}
                              {!item.is_read && (
                                <span className="absolute left-0 top-0 bottom-0 w-1 bg-blue-600" />
                              )}

                              {/* Event icon */}
                              <div
                                className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
                                  item.is_read ? "bg-slate-100" : "bg-white shadow-sm ring-1 ring-blue-200"
                                }`}
                              >
                                {eventIcon(item.event_type)}
                              </div>

                              {/* Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`text-[13px] font-medium truncate ${
                                      item.is_read ? "text-slate-700" : "text-slate-900"
                                    }`}
                                  >
                                    {item.user?.username || "System"}
                                  </span>
                                  {item.event_type && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-semibold bg-slate-100 text-slate-600 rounded uppercase tracking-wider">
                                      {item.event_type.replace(/[._]/g, " ")}
                                    </span>
                                  )}
                                </div>
                                <p
                                  className={`text-[12.5px] mt-0.5 line-clamp-2 ${
                                    item.is_read ? "text-slate-500" : "text-slate-800"
                                  }`}
                                >
                                  {item.message}
                                </p>
                                <p className="text-[10.5px] text-slate-400 mt-1">
                                  {formatRelative(item.createdAt)}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))
                  )}
                </div>

                {/* Footer — View all → Notification Management page */}
                <button
                  type="button"
                  onClick={() => {
                    setShowNotifications(false);
                    navigate("/notifications/manage");
                  }}
                  className="w-full px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border-t border-slate-100 text-[13px] font-medium text-blue-700 inline-flex items-center justify-center gap-1.5 transition-colors"
                >
                  View all notifications
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
              );
            })()}
          </div>

          <div className="relative profile-dropdown">
            <button
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              className="flex items-center gap-2 p-1 hover:bg-gray-100 rounded-lg"
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden">
                {userInfo?.profile_image ? (
                  <img
                    src={userInfo.profile_image}
                    alt={`${userInfo.name.givenName} ${userInfo.name.familyName}`}
                    className="w-full h-full object-cover"
                  />
                ) : userInfo ? (
                  <div className="w-full h-full bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {userInfo.name.givenName.charAt(0).toUpperCase()}
                      {userInfo.name.familyName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                ) : (
                  <div className="w-full h-full bg-blue-600 rounded-full flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              <div className="hidden sm:block text-left">
                {isLoading ? (
                  <div className="animate-pulse">
                    <div className="h-3 bg-gray-300 rounded w-24"></div>
                  </div>
                ) : userInfo ? (
                  // Name only on the header — email/username lives in the
                  // profile dropdown to keep the top bar clean.
                  <p className="text-sm font-medium text-gray-900">
                    {userInfo.name.givenName} {userInfo.name.familyName}
                  </p>
                ) : (
                  <p className="text-sm font-medium text-gray-900">User</p>
                )}
              </div>
              <ChevronDown className="w-4 h-4 text-gray-500" />
            </button>

            {showProfileMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200">
                <div className="p-3 border-b border-gray-100">
                  {userInfo ? (
                    <>
                      <p className="text-sm font-medium text-gray-900">
                        {userInfo.name.givenName} {userInfo.name.familyName}
                      </p>
                      <p className="text-xs text-gray-500">
                        {userInfo.emails.find((email) => email.primary)
                          ?.value || userInfo.userName}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-gray-900">User</p>
                      <p className="text-xs text-gray-500">Loading...</p>
                    </>
                  )}
                </div>
                <div className="p-2">
                  <button
                    className="flex items-center gap-3 w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg text-left"
                    type="button"
                    onClick={() => {
                      navigate("/profile");
                      setShowProfileMenu(false);
                    }}
                  >
                    <User className="w-4 h-4" />
                    Profile
                  </button>
                </div>
                <div className="p-2 border-t border-gray-100">
                  <button
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-sm rounded-lg transition-colors text-left ${
                      isLoggingOut
                        ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                        : "text-red-600 hover:bg-red-50"
                    }`}
                  >
                    {isLoggingOut ? (
                      <>
                        <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                        Logging out...
                      </>
                    ) : (
                      <>
                        <LogOut className="w-4 h-4" />
                        Logout
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
